import React from 'react'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import { v4 as uuidv4 } from 'uuid'
import { DefaultSeo } from 'next-seo'
import { scroller } from 'react-scroll'
import { useRouter } from 'next/router'
import { ProgressBar } from '../components'
import { LocationCtxProvider } from '../lib/context/Location'

// The Styles
import '../styles/main.scss'
import Tracker from 'lib/tracker/Tracker'
import { getPublicRuntimeConfig } from '../config/runtime'

const ROUTE_WITHOUT_DEFAULT_LAYOUT = ['/vacancies/[id]', '/vacancies/[id]/confirmation']

const DefaultLayout = dynamic(() => import('layouts/Default'))
const AnimatePresence = dynamic(() => import('framer-motion').then(mod => mod.AnimatePresence))
const ParallaxProvider = dynamic(() =>
  import('react-scroll-parallax').then(mod => mod.ParallaxProvider),
)

const MyApp = ({ Component, pageProps }) => {
  const config = getPublicRuntimeConfig()
  const router = useRouter()
  const [progressBarState, setProgressBarState] = React.useState({
    isRouteChanging: false,
    loadingKey: uuidv4().substr(0, 8),
    currentHash: '',
  })

  React.useEffect(() => {
    const routeChangeStartHandler = () =>
      setProgressBarState({
        ...progressBarState,
        isRouteChanging: true,
        loadingKey: uuidv4().substr(0, 8),
      })

    let timeoutID
    const routeChangeEndHandler = () => {
      setProgressBarState({
        ...progressBarState,
        isRouteChanging: false,
      })

      const hash = router.asPath.split('#')[1]

      if (typeof hash !== 'undefined' && hash !== null) {
        timeoutID = setTimeout(() => {
          scroller.scrollTo(hash, {
            duration: 800,
            delay: 0,
            smooth: 'easeInOutQuart',
            offset: 0,
          })
        }, 1000)
      }
    }
    router.events.on('routeChangeStart', routeChangeStartHandler)
    router.events.on('routeChangeComplete', routeChangeEndHandler)
    router.events.on('routeChangeError', routeChangeEndHandler)

    return () => {
      router.events.off('routeChangeStart', routeChangeStartHandler)
      router.events.off('routeChangeComplete', routeChangeEndHandler)
      router.events.off('routeChangeError', routeChangeEndHandler)
      if (timeoutID !== undefined) {
        clearTimeout(timeoutID)
      }
    }
  }, [progressBarState, progressBarState.isRouteChanging, router.asPath, router.events])

  return (
    <LocationCtxProvider locations={pageProps.locations}>
      <Tracker />
      <ProgressBar {...progressBarState} />
      <DefaultSeo
        title={pageProps.meta?.title}
        description={pageProps.meta?.description}
        canonical={'https://' + config.host + router.basePath + router.pathname.replace(/\/$/, '')}
        openGraph={{
          type: 'website',
          locale: 'en',
          site_name: 'Traveloka Career',
          description: pageProps.meta?.description,
          title: pageProps.meta?.title,
          images: [
            {
              url: 'https://ik.imagekit.io/tvlk/career-asset/public/banner-1.png?tr=w-1200',
              width: 1200,
              height: 630,
              alt: 'Traveloka careers image',
            },
            {
              url:
                'https://ik.imagekit.io/tvlk/career-asset/public/banner-1.png?tr=w-630,h-630,fo-left',
              width: 630,
              height: 630,
              alt: 'Traveloka careers image square',
            },
          ],
        }}
        twitter={{
          handle: '@travelokaeng',
          site: '@traveloka',
          cardType: 'summary_large_image',
        }}
      />
      <Head>
        <title>Traveloka Career</title>
        {/* <link rel="stylesheet" href="https://use.typekit.net/dia1yiv.css" async /> */}
        <link rel="preconnect" href={config.imagesExternalHost} />
      </Head>
      <ParallaxProvider>
        {ROUTE_WITHOUT_DEFAULT_LAYOUT.includes(router.route) ? (
          <AnimatePresence exitBeforeEnter>
            <Component {...pageProps} key={router.route} />
          </AnimatePresence>
        ) : (
          <DefaultLayout pageProps={pageProps}>
            <AnimatePresence exitBeforeEnter>
              <Component {...pageProps} key={router.route} />
            </AnimatePresence>
          </DefaultLayout>
        )}
      </ParallaxProvider>
    </LocationCtxProvider>
  )
}

export default MyApp
